import { Button, Card } from '@features/ui';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getPublicTerms,
  TermAndConditionGroupByTypeRead,
  TermsRes,
} from '@src/services/termsAndConditions';

type Props = {
  open: boolean;
  onClose: () => void;
};

const TermCard = ({ children }: { children: React.ReactNode }) => {
  return (
    <Card className="bg-card-500 flex flex-col gap-y-4 mb-2 p-3 relative pr-4 lg:pr-12">
      <div className="absolute top-5 right-2.5 w-[7px] h-[45px] bg-primary rounded-2xl" />
      {children}
    </Card>
  );
};

export const PubicTermAndConditionModal: React.FC<Props> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const [groups, setGroups] = useState<TermAndConditionGroupByTypeRead[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;

    let mounted = true;

    const loadTerms = async () => {
      try {
        setLoading(true);
        const res = await getPublicTerms();

        // Backend already returns data grouped and sorted by type.sort_order
        if (mounted) setGroups(res.data ?? []);
      } catch {
        console.error('Failed to load terms');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadTerms();

    return () => {
      mounted = false;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(0,0,0,0.2)] p-4">
      <div className="bg-card-300 rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-medium">{t('term.terms&Conditions')}</h2>
          <button
            aria-label="Close"
            className="text-gray-500 hover:cursor-pointer text-3xl leading-none"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="overflow-y-auto p-6">
          <Card className="bg-card-300 px-5 py-3 lg:px-8 lg:py-6">
            {loading ? (
              <div className="text-center py-8">
                <p className="text-sm">Loading terms...</p>
              </div>
            ) : groups.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm">No terms available.</p>
              </div>
            ) : (
              groups.map((group) => (
                <div key={group.type_id} className="mb-8">
                  <h3 className="text-base font-bold mb-3 text-primary uppercase tracking-wide">
                    {group.name}
                  </h3>
                  {group.terms.map((term: TermsRes) => (
                    <div key={term.id} className="mb-5">
                      <TermCard>
                        <p className="text-sm font-bold uppercase">
                          {term.name}
                        </p>
                        <div
                          dangerouslySetInnerHTML={{ __html: term.content }}
                          className={
                            'text-sm leading-6 whitespace-normal [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:my-3 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:my-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:my-2 [&_p]:my-2 [&_strong]:font-semibold [&_a]:underline [&_a]:text-primary [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2 [&_li]:my-1 [&_li::marker]:text-white'
                          }
                        />
                      </TermCard>
                    </div>
                  ))}
                </div>
              ))
            )}
          </Card>
        </div>
        <div className="p-6 border-t flex justify-center">
          <Button
            className="lg:min-w-[145px]"
            size={'lg'}
            variant={'gradient'}
            onClick={onClose}
          >
            {t('common.close', 'Close')}
          </Button>
        </div>
      </div>
    </div>
  );
};
